'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ActionMetadata } from '../types';
import type { MlinkInstance, MlinkStatus, UseMlinkOptions, RegistrationResult } from './types';
import { parseMlinkUrl } from '../utils';
import { validateActionMetadata } from '../validators';
import { REGISTRY_URL, REGISTRY_VALIDATE_ENDPOINT } from '../constants';

const DEFAULT_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

export function useMlink(
  url: string,
  options: UseMlinkOptions = {}
): MlinkInstance {
  const {
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    enabled = true,
    registryUrl = REGISTRY_URL,
    requireRegistration = true,
    allowPending = true,
    allowBlocked = false,
  } = options;

  const [status, setStatus] = useState<MlinkStatus>('idle');
  const [metadata, setMetadata] = useState<ActionMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [registration, setRegistration] = useState<RegistrationResult | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Extract action URL from mlink URL if needed
  const actionUrl = parseMlinkUrl(url) || url;

  // Validate against registry
  const validateRegistration = useCallback(async (): Promise<RegistrationResult | null> => {
    if (!actionUrl || !requireRegistration) return null;

    try {
      const validateUrl = `${registryUrl}${REGISTRY_VALIDATE_ENDPOINT}?url=${encodeURIComponent(actionUrl)}`;
      const response = await fetch(validateUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      const data = await response.json();
      return data as RegistrationResult;
    } catch (err) {
      console.error('Registry validation error:', err);
      // If registry is unreachable, we can either fail or allow
      // For now, we'll return a failure result
      return {
        isRegistered: false,
        status: null,
        error: 'Unable to validate against registry. Please try again later.',
      };
    }
  }, [actionUrl, registryUrl, requireRegistration]);

  const fetchMetadata = useCallback(async () => {
    if (!actionUrl || !enabled) return;

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setStatus('validating');
    setError(null);

    try {
      // Step 1: Validate against registry (if required)
      if (requireRegistration) {
        const registrationResult = await validateRegistration();
        setRegistration(registrationResult);

        if (registrationResult) {
          // Check if mlink is registered
          if (!registrationResult.isRegistered) {
            setError(registrationResult.error || 'This MLink is not registered.');
            setStatus('unregistered');
            return;
          }

          // Check if mlink is blocked
          if (registrationResult.status === 'blocked' && !allowBlocked) {
            setError('This MLink has been blocked for policy violations.');
            setStatus('blocked');
            return;
          }

          // Check if mlink is pending (and if we allow pending)
          if (registrationResult.status === 'pending' && !allowPending) {
            setError('This MLink is pending review and not yet available.');
            setStatus('error');
            return;
          }
        }
      }

      // Step 2: Fetch action metadata
      setStatus('loading');

      const response = await fetch(actionUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Validate the response
      const validation = validateActionMetadata(data);
      if (!validation.success) {
        throw new Error(`Invalid metadata: ${validation.error}`);
      }

      setMetadata(validation.data);
      setStatus('ready');
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Ignore abort errors
      }

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch mlink data';
      setError(errorMessage);
      setStatus('error');
    }
  }, [actionUrl, enabled, requireRegistration, validateRegistration, allowBlocked, allowPending]);

  // Initial fetch
  useEffect(() => {
    if (enabled) {
      fetchMetadata();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchMetadata, enabled]);

  // Set up refresh interval
  useEffect(() => {
    if (!enabled || !refreshInterval) return;

    intervalRef.current = setInterval(() => {
      fetchMetadata();
    }, refreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchMetadata, refreshInterval, enabled]);

  return {
    status,
    metadata,
    error,
    url: actionUrl,
    refresh: fetchMetadata,
    registration,
    isRegistered: registration?.isRegistered ?? false,
  };
}
